import React, { useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonSpinner,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
} from "@ionic/react";
import { checkmarkCircle, closeCircle, playOutline } from "ionicons/icons";
import { testCapgoHealthV8 } from "../../../services/test-capgo-health";

const HealthPluginTester: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const runTest = async () => {
    setIsRunning(true);
    setLogs([]);
    setResult(null);

    // Capture console.log for display
    const originalLog = console.log;
    const originalError = console.error;
    const capturedLogs: string[] = [];

    console.log = (...args: any[]) => {
      const message = args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      capturedLogs.push(message);
      setLogs([...capturedLogs]);
      originalLog(...args);
    };

    console.error = (...args: any[]) => {
      const message = '❌ ' + args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      capturedLogs.push(message);
      setLogs([...capturedLogs]);
      originalError(...args);
    };

    try {
      const testResult = await testCapgoHealthV8();
      setResult(testResult);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : String(error)
      });
    } finally {
      // Restore console
      console.log = originalLog;
      console.error = originalError;
      setIsRunning(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>Health Plugin Tester</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Test @capgo/capacitor-health v8.2.9</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <p className="text-sm mb-4">
              This will test all 8 active health data types plus HRV (new):
            </p>
            <IonList>
              <IonItem lines="none">
                <IonLabel>Steps, Heart Rate, Sleep</IonLabel>
              </IonItem>
              <IonItem lines="none">
                <IonLabel>Oxygen Saturation, Respiratory Rate</IonLabel>
              </IonItem>
              <IonItem lines="none">
                <IonLabel>Resting Heart Rate, HRV, Weight</IonLabel>
              </IonItem>
              <IonItem lines="none">
                <IonLabel>Workouts, Aggregated Steps</IonLabel>
              </IonItem>
            </IonList>

            <IonButton
              expand="block"
              onClick={runTest}
              disabled={isRunning}
              className="mt-4"
            >
              {isRunning ? (
                <>
                  <IonSpinner name="crescent" className="mr-2" />
                  Running Tests...
                </>
              ) : (
                <>
                  <IonIcon icon={playOutline} slot="start" />
                  Run Tests
                </>
              )}
            </IonButton>
          </IonCardContent>
        </IonCard>

        {result && (
          <IonCard color={result.success ? "success" : "danger"}>
            <IonCardHeader>
              <IonCardTitle className="flex items-center gap-2">
                <IonIcon
                  icon={result.success ? checkmarkCircle : closeCircle}
                  className="text-xl"
                />
                {result.success ? "Tests Completed" : "Tests Failed"}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {result.message && <p>{result.message}</p>}
              {result.error && (
                <p className="text-red-100">Error: {result.error}</p>
              )}
            </IonCardContent>
          </IonCard>
        )}

        {logs.length > 0 && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                Test Logs <IonBadge color="primary">{logs.length}</IonBadge>
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="bg-gray-900 text-white p-4 rounded font-mono text-xs overflow-x-auto max-h-96 overflow-y-auto">
                {logs.map((log, index) => (
                  <div key={index} className="mb-1 whitespace-pre-wrap break-words">
                    {log}
                  </div>
                ))}
              </div>
            </IonCardContent>
          </IonCard>
        )}

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Instructions</IonCardTitle>
          </IonCardHeader>
          <IonCardContent className="text-sm">
            <ol className="list-decimal list-inside space-y-2">
              <li>Run the test above to verify @capgo/capacitor-health v8.2.9 works</li>
              <li>Check the logs for data counts from each test</li>
              <li>Compare with existing @perfood implementation</li>
              <li>If all tests pass with similar data counts, proceed with migration</li>
            </ol>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  );
};

export default HealthPluginTester;
